import React, { useState, useEffect } from "react";
import { Row, Col, Card, Button, Skeleton, Avatar, Badge } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import useRequest from "../../../hooks/useRequest";
import { ShowToast, Severty } from "../../../helper/toast";
import apiPath from "../../../constants/apiPath";
import { ArrowLeftOutlined, EyeOutlined } from "@ant-design/icons";
import moment from "moment";
import lang from "../../../helper/lang";
import { capitalize } from "../../../helper/functions";

import ViewFollowersModal from "./ViewFollowersModal";
import ViewFollowingModal from "./ViewFollowingModal";

function View() {
  const params = useParams();
  const navigate = useNavigate();
  const { request } = useRequest();

  const [list, setList] = useState({});
  const [loading, setLoading] = useState(false);

  const [followersModalVisible, setFollowersModalVisible] = useState(false);
  const [followingModalVisible, setFollowingModalVisible] = useState(false);

  const fetchData = (id) => {
    setLoading(true);
    request({
      url: `${apiPath.viewUser}/${id}`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        setList(data?.data?.data || {});
      },
      onError: (error) => {
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  useEffect(() => {
    fetchData(params.id);
  }, [params.id]);

  return (
    <>
      <div className="profile-container">
        <Button className="back-btn" onClick={() => navigate(-1)}>
          <ArrowLeftOutlined /> Back
        </Button>

        {loading ? (
          <Card style={{ borderRadius: 16 }}>
            {[1, 2, 3].map((item) => <Skeleton active key={item} />)}
          </Card>
        ) : (
          <>
            {/* Header Card */}
            <div className="header-card">
              <div className="header-flex">
                <div className="user-info-section">
                  <div className="avatar-wrapper">
                    {list.avatar ? (
                      <Avatar src={list.avatar} size={100} />
                    ) : (
                      <Avatar style={{ backgroundColor: "#fa541c" }} size={100}>
                        {list?.name?.charAt(0) || "-"}
                      </Avatar>
                    )}
                  </div>
                  <div className="user-text">
                    <h2>
                      {list.name || "-"}
                      <span style={{ fontSize: "14px" }}>
                        {list.status === "active" ? (
                          <Badge status="success" text={<span style={{ color: "#52c41a", fontWeight: 600 }}>Active</span>} />
                        ) : (
                          <Badge status="error" text={<span style={{ color: "#ff4d4f", fontWeight: 600 }}>Inactive</span>} />
                        )}
                      </span>
                    </h2>
                    <div className="username-tag">@{list.username || "username"}</div>
                    <div style={{ color: "rgba(255,255,255,0.85)", fontSize: "14px", fontWeight: 500 }}>
                      {list.bio || "No bio available"}
                    </div>
                  </div>
                </div>

                <div className="stats-buttons-group">
                  <Button className="stat-btn" onClick={() => setFollowersModalVisible(true)}>
                    Followers ({list?.totalFollowers || 0})
                  </Button>
                  <Button className="stat-btn" onClick={() => setFollowingModalVisible(true)}>
                    Following ({list?.totalFollowings || 0})
                  </Button>
                </div>
              </div>
            </div>

            {/* Metrics Row */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={12} sm={8} md={6}>
                <div className="metric-card">
                  <h5>Total Posts</h5>
                  <h3>{list?.totalPosts ?? 0}</h3>
                </div>
              </Col>
              <Col xs={12} sm={8} md={6}>
                <div className="metric-card">
                  <h5>Total Comments</h5>
                  <h3>{list?.totalComments ?? 0}</h3>
                </div>
              </Col>
              <Col xs={12} sm={8} md={6}>
                <div className="metric-card">
                  <h5>Points</h5>
                  <h3>{list?.points ?? 0}</h3>
                </div>
              </Col>
              <Col xs={12} sm={8} md={6}>
                <div className="metric-card">
                  <h5>Registered On</h5>
                  <h3>{list.createdAt ? moment(list.createdAt).format("DD-MMM-YYYY") : "-"}</h3>
                </div>
              </Col>
            </Row>

            {/* Main Info Blocks */}
            <Row gutter={[24, 24]}>
              <Col xs={24} md={12}>
                <div className="info-card">
                  <div className="card-header-title">Personal Details</div>
                  
                  <div className="detail-row">
                    <div className="detail-label">Email Address</div>
                    <div className="detail-value">{list.email || "-"}</div>
                  </div>
                  
                  <div className="detail-row">
                    <div className="detail-label">Mobile Number</div>
                    <div className="detail-value">
                      {list.countryCode ? `+${list.countryCode}-` : ""}
                      {list.phone || "-"}
                    </div>
                  </div>
                  
                  <div className="detail-row">
                    <div className="detail-label">Location</div>
                    <div className="detail-value">
                      {list.location ? [list.location.city, list.location.state, list.location.country].filter(Boolean).join(", ") : "-"}
                    </div>
                  </div>

                  <div className="detail-row">
                    <div className="detail-label">Login Streak</div>
                    <div className="detail-value">{list.loginStreak || 0} days</div>
                  </div>

                  <div className="detail-row">
                    <div className="detail-label">Profile Views</div>
                    <div className="detail-value">{list.profileViewCount || 0}</div>
                  </div>
                </div>
              </Col>

              <Col xs={24} md={12}>
                <div className="info-card">
                  <div className="card-header-title">Cricket Details</div>
                  
                  <div className="detail-row">
                    <div className="detail-label">Cricket Role</div>
                    <div className="detail-value">
                      {list.role ? (
                        <span className="role-badge">{capitalize(list.role.name)}</span>
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>
                  
                  <div className="detail-row">
                    <div className="detail-label">Skills</div>
                    <div className="detail-value" style={{ display: "flex", flexWrap: "wrap", paddingTop: "4px" }}>
                      {list?.skills?.length > 0 ? (
                        list.skills.map((skill) => (
                          <span key={skill?.id || skill?._id} className="skill-tag">
                            {skill?.name}
                          </span>
                        ))
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>

                  <div className="detail-row">
                    <div className="detail-label">Age Category</div>
                    <div className="detail-value">{list.ageCategory || "-"}</div>
                  </div>

                  <div className="detail-row">
                    <div className="detail-label">Account Visibility</div>
                    <div className="detail-value">{list.accountVisibility || "PUBLIC"}</div>
                  </div>
                </div>
              </Col>
            </Row>

            {/* Coach Details */}
            {list?.isCoach && list?.coachProfile && (
              <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
                <Col span={24}>
                  <div className="info-card">
                    <div className="card-header-title">Coach Profile Information</div>
                    
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="info-item-label">Full Name</span>
                        <span className="info-item-value">{list.coachProfile.fullName || "-"}</span>
                      </div>
                      
                      <div className="info-item">
                        <span className="info-item-label">Experience Level</span>
                        <span className="info-item-value">{list.coachProfile.experienceLevel || "-"}</span>
                      </div>
                      
                      <div className="info-item">
                        <span className="info-item-label">Fee Structure</span>
                        <span className="info-item-value">
                          {list.coachProfile.feeStructure ? (
                            <span>
                              <span className="fee-tag">{list.coachProfile.feeStructure}</span>
                              {list.coachProfile.feeAmount ? ` (Amount: ${list.coachProfile.feeAmount})` : ""}
                            </span>
                          ) : (
                            "-"
                          )}
                        </span>
                      </div>

                      <div className="info-item">
                        <span className="info-item-label">Account Type</span>
                        <span className="info-item-value" style={{ textTransform: "capitalize" }}>
                          {list.coachProfile.accountType || "-"}
                        </span>
                      </div>

                      <div className="info-item">
                        <span className="info-item-label">Verification Status</span>
                        <span className="info-item-value">
                          {list.coachProfile.isVerified ? (
                            <Badge status="success" text={<span style={{ color: "#52c41a", fontWeight: 600 }}>Verified</span>} />
                          ) : (
                            <Badge status="warning" text={<span style={{ color: "#faad14", fontWeight: 600 }}>Not Verified</span>} />
                          )}
                        </span>
                      </div>
                      
                      <div className="info-item">
                        <span className="info-item-label">Contact Preference</span>
                        <span className="info-item-value" style={{ textTransform: "capitalize" }}>
                          {list.coachProfile.contactPreference || "-"}
                        </span>
                      </div>
                      
                      <div className="info-item">
                        <span className="info-item-label">Phone Number</span>
                        <span className="info-item-value">
                          {list.coachProfile.phoneNumber
                            ? `${list.coachProfile.countryCode ? `+${list.coachProfile.countryCode}-` : ""}${list.coachProfile.phoneNumber}`
                            : "-"}
                        </span>
                      </div>

                      <div className="info-item">
                        <span className="info-item-label">Coaching Location</span>
                        <span className="info-item-value">
                          {list.coachProfile.location
                            ? [list.coachProfile.location.city, list.coachProfile.location.state, list.coachProfile.location.country].filter(Boolean).join(", ")
                            : "-"}
                        </span>
                      </div>

                      {list.coachProfile.academyAffiliation && (
                        <div className="info-item">
                          <span className="info-item-label">Academy Affiliation</span>
                          <span className="info-item-value">
                            {list.coachProfile.academyAffiliation.academyName || "-"}
                          </span>
                        </div>
                      )}
                    </div>

                    <Row gutter={[24, 24]}>
                      <Col xs={24} md={8}>
                        <div style={{ fontWeight: 600, color: "#8c8c8c", fontSize: "12px", textTransform: "uppercase", marginBottom: "10px" }}>
                          Specialties
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                          {list.coachProfile.specialties?.length > 0 ? (
                            list.coachProfile.specialties.map((spec) => (
                              <span key={spec} className="specialty-tag">{spec.replace("_", " ")}</span>
                            ))
                          ) : (
                            "-"
                          )}
                        </div>
                      </Col>

                      <Col xs={24} md={8}>
                        <div style={{ fontWeight: 600, color: "#8c8c8c", fontSize: "12px", textTransform: "uppercase", marginBottom: "10px" }}>
                          Certifications
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                          {list.coachProfile.certifications?.length > 0 ? (
                            list.coachProfile.certifications.map((cert) => (
                              <span key={cert} className="cert-tag">{cert}</span>
                            ))
                          ) : (
                            "-"
                          )}
                        </div>
                      </Col>

                      <Col xs={24} md={8}>
                        <div style={{ fontWeight: 600, color: "#8c8c8c", fontSize: "12px", textTransform: "uppercase", marginBottom: "10px" }}>
                          Availability
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                          {list.coachProfile.availability?.length > 0 ? (
                            list.coachProfile.availability.map((day) => (
                              <span key={day} className="availability-tag">{day}</span>
                            ))
                          ) : (
                            "-"
                          )}
                        </div>
                      </Col>
                    </Row>

                    {list.coachProfile.shortBio && (
                      <div className="bio-box">
                        <strong>Short Bio:</strong> "{list.coachProfile.shortBio}"
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            )}

            {/* Academy Details */}
            {list?.isAcademy && list?.academyProfile && (
              <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
                <Col span={24}>
                  <div className="info-card">
                    <div className="card-header-title">Academy Profile Information</div>
                    
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="info-item-label">Academy Name</span>
                        <span className="info-item-value">{list.academyProfile.academyName || list.academyProfile.fullName || list.academyProfile.name || "-"}</span>
                      </div>
                      
                      <div className="info-item">
                        <span className="info-item-label">Registration / Estd. Year</span>
                        <span className="info-item-value">{list.academyProfile.foundedYear || list.academyProfile.establishedYear || list.academyProfile.experienceLevel || "-"}</span>
                      </div>
                      
                      <div className="info-item">
                        <span className="info-item-label">Fee Structure</span>
                        <span className="info-item-value">
                          {list.academyProfile.feeStructure ? (
                            <span>
                              <span className="fee-tag">{list.academyProfile.feeStructure}</span>
                              {list.academyProfile.feeAmount ? ` (Amount: ${list.academyProfile.feeAmount})` : ""}
                            </span>
                          ) : (
                            "-"
                          )}
                        </span>
                      </div>

                      <div className="info-item">
                        <span className="info-item-label">Facilities</span>
                        <span className="info-item-value">{list.academyProfile.facilities || "-"}</span>
                      </div>

                      <div className="info-item">
                        <span className="info-item-label">Contact Preference</span>
                        <span className="info-item-value" style={{ textTransform: "capitalize" }}>
                          {list.academyProfile.contactPreference || "-"}
                        </span>
                      </div>
                      
                      <div className="info-item">
                        <span className="info-item-label">Phone Number</span>
                        <span className="info-item-value">
                          {list.academyProfile.phoneNumber || list.academyProfile.phone
                            ? `${list.academyProfile.countryCode ? `+${list.academyProfile.countryCode}-` : ""}${list.academyProfile.phoneNumber || list.academyProfile.phone}`
                            : "-"}
                        </span>
                      </div>

                      <div className="info-item">
                        <span className="info-item-label">Location</span>
                        <span className="info-item-value">
                          {list.academyProfile.location
                            ? [list.academyProfile.location.city, list.academyProfile.location.state, list.academyProfile.location.country || list.academyProfile.location.address].filter(Boolean).join(", ")
                            : "-"}
                        </span>
                      </div>

                      <div className="info-item">
                        <span className="info-item-label">Verification Status</span>
                        <span className="info-item-value">
                          {list.academyProfile.isVerified ? (
                            <Badge status="success" text={<span style={{ color: "#52c41a", fontWeight: 600 }}>Verified</span>} />
                          ) : (
                            <Badge status="warning" text={<span style={{ color: "#faad14", fontWeight: 600 }}>Not Verified</span>} />
                          )}
                        </span>
                      </div>
                    </div>

                    <Row gutter={[24, 24]}>
                      <Col xs={24} md={8}>
                        <div style={{ fontWeight: 600, color: "#8c8c8c", fontSize: "12px", textTransform: "uppercase", marginBottom: "10px" }}>
                          Specialties / Sports Offered
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                          {Array.isArray(list.academyProfile.specialties) && list.academyProfile.specialties.length > 0 ? (
                            list.academyProfile.specialties.map((spec) => (
                              <span key={spec} className="specialty-tag">{spec.replace("_", " ")}</span>
                            ))
                          ) : list.academyProfile.specialties ? (
                            <span className="specialty-tag">{list.academyProfile.specialties.replace("_", " ")}</span>
                          ) : (
                            "-"
                          )}
                        </div>
                      </Col>

                      <Col xs={24} md={8}>
                        <div style={{ fontWeight: 600, color: "#8c8c8c", fontSize: "12px", textTransform: "uppercase", marginBottom: "10px" }}>
                          Age Groups Coached
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                          {Array.isArray(list.academyProfile.ageGroupsCoached) && list.academyProfile.ageGroupsCoached.length > 0 ? (
                            list.academyProfile.ageGroupsCoached.map((group) => (
                              <span key={group} className="availability-tag">{group.replace("_", " ")}</span>
                            ))
                          ) : list.academyProfile.ageGroupsCoached ? (
                            <span className="availability-tag">{list.academyProfile.ageGroupsCoached.replace("_", " ")}</span>
                          ) : (
                            "-"
                          )}
                        </div>
                      </Col>

                      <Col xs={24} md={8}>
                        <div style={{ fontWeight: 600, color: "#8c8c8c", fontSize: "12px", textTransform: "uppercase", marginBottom: "10px" }}>
                          Operating Days / Timings
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                          {Array.isArray(list.academyProfile.availability) && list.academyProfile.availability.length > 0 ? (
                            list.academyProfile.availability.map((day) => (
                              <span key={day} className="availability-tag">{day.replace("_", " ")}</span>
                            ))
                          ) : list.academyProfile.availability ? (
                            <span className="availability-tag">{list.academyProfile.availability.replace("_", " ")}</span>
                          ) : (
                            "-"
                          )}
                        </div>
                      </Col>
                    </Row>

                    {(list.academyProfile.shortDescription || list.academyProfile.shortBio || list.academyProfile.bio) && (
                      <div className="bio-box" style={{ borderLeftColor: "#1890ff" }}>
                        <strong>Description:</strong> "{list.academyProfile.shortDescription || list.academyProfile.shortBio || list.academyProfile.bio}"
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      <ViewFollowersModal
        visible={followersModalVisible}
        onClose={() => setFollowersModalVisible(false)}
        userId={params.id}
      />
      <ViewFollowingModal
        visible={followingModalVisible}
        onClose={() => setFollowingModalVisible(false)}
        userId={params.id}
      />
    </>
  );
}

export default View;
